import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { doc, getDoc } from "firebase/firestore";

import { db } from "../services/firebase.config";
import { useAuth } from "../services/AuthContext";
import dataCache from "../services/DataCache";
import remoteQuizService from "../services/RemoteQuizService";
import { White, DarkGrey, White08, Black } from "./theme/colors";

const SocialProofContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-top: -7px;
  gap: 0px;
  padding: 0 15px;
  position: relative;
  z-index: 10;
`;

const FriendCirclesContainer = styled.div`
  display: flex;
  align-items: center;
`;

const FriendCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid ${Black};
  margin-left: ${(props) => (props.$index > 0 ? "-8px" : "0")};
  z-index: ${(props) => 10 - props.$index};
  position: relative;
  overflow: hidden;
  background-color: ${DarkGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${White};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const FriendProfilePic = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AdditionalCount = styled.div`
  margin-left: 8px;
  font-size: 12px;
  color: ${White08};
  font-weight: 600;
  background: ${Black};
  padding: 2px 6px;
  border-radius: 10px;
  backdrop-filter: blur(4px);
`;

const QuizSocialProof = ({ quizType }) => {
  const { currentUserState } = useAuth();
  const [friendsWithQuiz, setFriendsWithQuiz] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch friends who have completed this specific quiz
  const fetchFriendsWithQuiz = useCallback(async () => {
    if (!currentUserState?.uid || !quizType) return;

    setLoading(true);
    try {
      // Get current user's profile to access friends list
      const userProfileKey = `user-profile-${currentUserState.uid}`;
      let userProfile = dataCache.get(userProfileKey);

      if (!userProfile) {
        const userProfileRef = doc(db, "public-profiles", currentUserState.uid);
        const userProfileSnap = await getDoc(userProfileRef);
        if (userProfileSnap.exists()) {
          userProfile = userProfileSnap.data();
          dataCache.set(userProfileKey, userProfile, 5 * 60 * 1000);
        }
      }

      if (!userProfile?.friends?.length) {
        setFriendsWithQuiz([]);
        return;
      }

      // Get config from remote
      const config = await remoteQuizService.getQuizConfig(quizType);
      if (!config) return;

      // Check cache for friends with this quiz
      const cacheKey = `friends-with-${quizType}-${currentUserState.uid}`;
      let cachedResult = dataCache.get(cacheKey);

      if (cachedResult) {
        setFriendsWithQuiz(cachedResult);
        return;
      }

      // Check each friend to see if they've completed this quiz
      const friendPromises = userProfile.friends.map(async (friendId) => {
        try {
          // Get friend's profile
          let friendProfile = dataCache.getUserProfile(friendId);
          if (!friendProfile) {
            const friendProfileRef = doc(db, "public-profiles", friendId);
            const friendProfileSnap = await getDoc(friendProfileRef);
            if (friendProfileSnap.exists()) {
              friendProfile = friendProfileSnap.data();
              dataCache.setUserProfile(friendId, friendProfile);
            }
          }

          if (!friendProfile) return null;

          // Check if friend has a quiz ID for this quiz type
          const quizId = friendProfile[config.databaseField];
          if (!quizId) return null;

          // Check if the quiz is completed
          const quizCacheKey = `quiz-completion-${quizType}-${friendId}`;
          let quizResult = dataCache.get(quizCacheKey);

          if (!quizResult) {
            const quizDocRef = doc(db, "quiz-answers", quizId);
            const quizDoc = await getDoc(quizDocRef);
            const isCompleted =
              quizDoc.exists() && quizDoc.data().status === "completed";

            quizResult = { completed: isCompleted };
            dataCache.set(quizCacheKey, quizResult, 3 * 60 * 1000);
          }

          if (quizResult.completed) {
            return {
              id: friendId,
              displayName: friendProfile.displayName,
              photoURL: friendProfile.photoURL,
            };
          }

          return null;
        } catch (error) {
          console.warn(
            `Error checking quiz completion for friend ${friendId}:`,
            error
          );
          return null;
        }
      });

      const results = await Promise.all(friendPromises);
      const completedFriends = results.filter(Boolean);

      // Cache the result for 3 minutes
      dataCache.set(cacheKey, completedFriends, 3 * 60 * 1000);
      setFriendsWithQuiz(completedFriends);
    } catch (error) {
      console.error("Error fetching friends with quiz:", error);
      setFriendsWithQuiz([]);
    } finally {
      setLoading(false);
    }
  }, [currentUserState?.uid, quizType]);

  useEffect(() => {
    fetchFriendsWithQuiz();
  }, [fetchFriendsWithQuiz]);

  // Don't render anything while loading or if no friends have taken the quiz
  if (loading || friendsWithQuiz.length === 0) {
    return null;
  }

  const displayFriends = friendsWithQuiz.slice(0, 5);
  const additionalCount = friendsWithQuiz.length - displayFriends.length;

  return (
    <SocialProofContainer>
      <FriendCirclesContainer>
        {displayFriends.map((friend, index) => (
          <FriendCircle key={friend.id} $index={index}>
            {friend.photoURL ? (
              <FriendProfilePic
                src={friend.photoURL}
                alt={friend.displayName}
              />
            ) : (
              <span>
                {friend.displayName ? friend.displayName[0].toUpperCase() : "?"}
              </span>
            )}
          </FriendCircle>
        ))}
      </FriendCirclesContainer>

      {additionalCount > 0 && (
        <AdditionalCount>
          +{additionalCount} other{additionalCount === 1 ? "" : "s"}
        </AdditionalCount>
      )}
    </SocialProofContainer>
  );
};

export default QuizSocialProof;
